import axios from 'axios';
import { MNSproCloudApi } from '..';
import {
  ClassBasicResponse,
  CourseBasicResponse,
  PaginatedOfClassBasicResponse,
  PaginatedOfCourseBasicResponse,
  Person,
  ResourceResponse,
  Substitution,
  SubstitutionCreate,
  SubstitutionMotive,
  TimePatternResponse,
  TimeTableLessonWithSubstitution,
  TimeTableOverview,
} from '../api/v2';

const getTimeTableApi = (authToken: string) =>
  MNSproCloudApi.V2.TimeTableApiFactory(
    MNSproCloudApi.V2.createConfiguration(authToken),
  );

const getEmptyTimetableOverview = () => ({
  timeTableData: [],
});

export class TimetableService {
  /**
   * Gets the timetable for the specified room id.
   * The backend returns data from all courses which take place in the given room.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} roomId
   * @param {string} startDate
   * @param {number} days
   * @return {*}  {Promise<TimeTableOverview>}
   * @memberof TimetableService
   */
  static async getRoomTimetable(
    authToken: string,
    organizationId: string,
    roomId: string,
    startDate: string,
    days: number,
  ): Promise<TimeTableOverview> {
    const api = getTimeTableApi(authToken);
    const response = await api.getTimeTableOfRoom(
      organizationId,
      roomId,
      startDate,
      days,
      undefined,
      {
        validateStatus: (status: number) => status === 200 || status === 404,
      },
    );

    if (response.status === 404) {
      return getEmptyTimetableOverview();
    }

    return response.data;
  }

  /**
   * Gets the timetable for the given group (ie. classes, courses).
   */
  static async getGroupTimetable(
    authToken: string,
    organizationId: string,
    groupId: string,
    startDate: string,
    days: number,
  ): Promise<TimeTableOverview> {
    const api = getTimeTableApi(authToken);
    const response = await api.getTimeTableOfGroup(
      organizationId,
      groupId,
      days,
      startDate,
      undefined,
      {
        validateStatus: (status: number) => status === 200 || status === 404,
      },
    );

    if (response.status === 404) {
      return getEmptyTimetableOverview();
    }

    return response.data;
  }

  /**
   * Gets the timetable for the given user id.
   */
  static async getUserTimetable(
    authToken: string,
    organizationId: string,
    userId: string,
    startDate: string,
    days: number,
  ): Promise<TimeTableOverview> {
    const api = getTimeTableApi(authToken);
    const response = await api.getTimeTableOfUser(
      organizationId,
      userId,
      days,
      startDate,
      undefined,
      {
        validateStatus: (status: number) => status === 200 || status === 404,
      },
    );

    if (response.status === 404) {
      return getEmptyTimetableOverview();
    }

    return response.data;
  }

  /**
   * Loads a list of all teachers
   */
  static async getTimetableTeachers(
    authToken: string,
    organizationId: string,
  ): Promise<Person[]> {
    const api = getTimeTableApi(authToken);
    return (await api.getTeachers(organizationId)).data;
  }

  /**
   * Loads a list of all students
   */
  static async getTimetableStudents(
    authToken: string,
    organizationId: string,
  ): Promise<Person[]> {
    const api = getTimeTableApi(authToken);
    return (await api.getStudents(organizationId)).data;
  }

  /**
   * Loads a list of all rooms
   */
  static async getAllTimetableRooms(
    authToken: string,
    organizationId: string,
  ): Promise<ResourceResponse[]> {
    const api = getTimeTableApi(authToken);
    const response = await api.getRooms(organizationId);

    return response.data;
  }

  static async getAllClasses(
    authToken: string,
    organizationId: string,
  ): Promise<ClassBasicResponse[]> {
    const api = getTimeTableApi(authToken);
    const response = await api.getClasses(organizationId);

    let nextLink: string | null | undefined = response.data.nextLink;
    let classes: ClassBasicResponse[] = response.data.value;
    while (nextLink) {
      const pageResponse = await axios.get<PaginatedOfClassBasicResponse>(
        nextLink,
        {
          headers: {
            authorization: 'Bearer ' + authToken,
          },
        },
      );
      classes = classes.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return classes;
  }

  static async getAllCourses(
    authToken: string,
    organizationId: string,
  ): Promise<CourseBasicResponse[]> {
    const api = getTimeTableApi(authToken);
    const response = await api.getCourses(organizationId);

    let nextLink: string | null | undefined = response.data.nextLink;
    let courses: CourseBasicResponse[] = response.data.value;
    while (nextLink) {
      const pageResponse = await axios.get<PaginatedOfCourseBasicResponse>(
        nextLink,
        {
          headers: {
            authorization: 'Bearer ' + authToken,
          },
        },
      );
      courses = courses.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return courses;
  }

  static async getLessonById(
    authToken: string,
    organizationId: string,
    lessonId: string,
  ): Promise<TimeTableLessonWithSubstitution> {
    const api = getTimeTableApi(authToken);
    const response = await api.getLessonWithSubstitution(
      organizationId,
      lessonId,
    );

    return response.data;
  }

  static async create(
    authToken: string,
    organizationId: string,
    lessonId: string,
    motive: SubstitutionMotive[],
    remark: string | null,
    teacherId: string | null,
    roomId: string | null,
    moveTo: string | null,
  ): Promise<Substitution> {
    const api = getTimeTableApi(authToken);
    const substitutionCreate: SubstitutionCreate = {
      motive,
      remark,
      teacherId,
      roomId,
      moveTo,
    };

    const response = await api.createLessonSubstitution(
      organizationId,
      lessonId,
      substitutionCreate,
    );

    return response.data;
  }

  static async getTimePattern(
    authToken: string,
    organizationId: string,
  ): Promise<TimePatternResponse> {
    const api = getTimeTableApi(authToken);
    const response = await api.getTimePattern(organizationId);

    return response.data as TimePatternResponse;
  }

  static async deleteSubstitution(
    authToken: string,
    organizationId: string,
    lessonId: string,
  ) {
    const api = getTimeTableApi(authToken);
    await api.deleteLessonSubstitution(organizationId, lessonId);
  }
}
